import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Typography,
    useTheme
} from '@mui/material';
import React, { useState } from 'react';
import { LessonPlan } from '../../object-actions/types/types';
import LessonPlanRecorder from './LessonPlanRecorder';
import LessonPlanSlideDeck from './LessonPlanSlideDeck';
import LessonRecordingsModal from './LessonRecordingsModal';

interface LessonPlanCardProps {
    plan: LessonPlan;
    planIndex: number;
    expanded: boolean;
    onChange: (event: React.SyntheticEvent<Element, Event>, expanded: boolean) => void;
    cardRef?: (element: HTMLDivElement | null) => void;
    lessonPlanId?: number | null;
    onRecordingChange?: (isRecording: boolean) => void;
    showRecorder?: boolean;
    coachContentId?: number | null;
}

const LessonPlanCard: React.FC<LessonPlanCardProps> = ({
    plan,
    planIndex,
    expanded,
    onChange,
    cardRef,
    lessonPlanId,
    onRecordingChange,
    showRecorder = false,
    coachContentId
}) => {
    const theme = useTheme();
    const [recordingsModalOpen, setRecordingsModalOpen] = useState(false);

    const headerText = plan.topic || `Lesson Plan ${planIndex + 1}`;
    const subtitle = plan.objective ? plan.objective : null;

    return (
        <>
            <Accordion
                ref={cardRef}
                expanded={expanded}
                onChange={onChange}
                sx={{
                    mb: 2,
                    '&:before': {
                        display: 'none',
                    },
                    boxShadow: 2,
                    borderRadius: 2,
                    overflow: 'hidden',
                    '&.Mui-expanded': {
                        margin: '0 0 16px 0',
                    }
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(25, 118, 210, 0.1)'
                            : 'rgba(25, 118, 210, 0.05)',
                        '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(25, 118, 210, 0.15)'
                                : 'rgba(25, 118, 210, 0.08)',
                        },
                        '&.Mui-expanded': {
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                        minHeight: 64,
                        '& .MuiAccordionSummary-content': {
                            my: 2
                        }
                    }}
                >
                    <Box sx={{ width: '100%' }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 'bold',
                                color: 'text.primary',
                            }}
                        >
                            {headerText}
                        </Typography>
                        {subtitle && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails
                    sx={{
                        p: 1,
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.02)'
                            : 'rgba(0, 0, 0, 0.01)',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {showRecorder && lessonPlanId && onRecordingChange && (
                            <LessonPlanRecorder
                                lessonPlanId={lessonPlanId}
                                onRecordingChange={onRecordingChange}
                            />
                        )}
                        {coachContentId && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setRecordingsModalOpen(true)}
                            >
                                View Recordings
                            </Button>
                        )}
                    </Box>
                    <LessonPlanSlideDeck plan={plan} />
                </AccordionDetails>
            </Accordion>
            {coachContentId && (
                <LessonRecordingsModal
                    open={recordingsModalOpen}
                    onClose={() => setRecordingsModalOpen(false)}
                    coachContentId={coachContentId}
                />
            )}
        </>
    );
};

export default LessonPlanCard;

